import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, Send, PawPrint, CheckCircle2, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TestimonialForm = () => {
  const { token } = useParams();
  const [prefill, setPrefill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', location: '', role: '', text: '', rating: 5 });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/testimonial-form/${token}`);
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(j.detail || `HTTP ${r.status}`);
        }
        const data = await r.json();
        setPrefill(data);
        setForm(f => ({
          ...f,
          name: data.name || '',
          location: data.location || '',
          role: data.role || '',
        }));
      } catch (e) {
        setError(e.message || 'Could not load the form.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.text.trim()) {
      alert('Please share a few words about your experience.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/testimonial-form/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      setSubmitted(true);
    } catch (e) {
      alert(`Failed to submit: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <PageShell><p className="text-center text-foreground/60">Loading…</p></PageShell>;
  }
  if (error) {
    return (
      <PageShell>
        <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div>
            <strong>Could not load this form.</strong>
            <div className="mt-1">{error}</div>
            <div className="mt-2 text-red-600/80">If you believe this is an error, please email us at <a className="underline" href="mailto:pawsome.ky@gmail.com">pawsome.ky@gmail.com</a>.</div>
          </div>
        </div>
      </PageShell>
    );
  }
  if (submitted) {
    return (
      <PageShell>
        <div className="text-center py-8" data-testid="testimonial-form-thanks">
          <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Thank you!</h1>
          <p className="text-foreground/70">Your testimonial has been received and will appear on the site once approved.</p>
          <Link to="/" className="inline-block mt-6 text-primary underline">Return to Pawsome Ventures →</Link>
        </div>
      </PageShell>
    );
  }

  const kindLabel = (prefill?.kind || 'collaborator');
  return (
    <PageShell>
      <div className="mb-6" data-testid="testimonial-form">
        <div className="text-xs uppercase tracking-wide text-primary/80 font-semibold mb-1">{kindLabel} testimonial</div>
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Share your experience</h1>
        <p className="text-sm text-foreground/70 mt-1">Thanks for taking a minute — it really helps future pet owners feel confident about their choice.</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="Your name">
          <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border border-border rounded-lg text-sm" data-testid="tf-name" />
        </Field>
        <Field label={kindLabel === 'client' ? 'Location (e.g. UK → USA)' : 'Location (e.g. Cayman Islands)'}>
          <input required value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} className="w-full px-3 py-2 border border-border rounded-lg text-sm" data-testid="tf-location" />
        </Field>
        {kindLabel !== 'client' && (
          <Field label="Role / Company">
            <input value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} placeholder="e.g. Broker at ABC Realty" className="w-full px-3 py-2 border border-border rounded-lg text-sm" data-testid="tf-role" />
          </Field>
        )}
        <Field label="Rating">
          <div className="flex items-center gap-1" data-testid="tf-rating">
            {[1,2,3,4,5].map(n => (
              <button
                key={n}
                type="button"
                onClick={() => setForm({ ...form, rating: n })}
                className="p-1"
                aria-label={`Rate ${n} out of 5`}
              >
                <Star className={`w-6 h-6 ${n <= form.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
              </button>
            ))}
          </div>
        </Field>
        <Field label="Your testimonial">
          <textarea
            required
            value={form.text}
            onChange={e => setForm({ ...form, text: e.target.value })}
            rows={6}
            maxLength={2000}
            placeholder="What was your experience like? What stood out?"
            className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            data-testid="tf-text"
          />
          <div className="text-xs text-foreground/50 mt-1">{form.text.length}/2000</div>
        </Field>
        <button
          type="submit"
          disabled={submitting}
          className="w-full sm:w-auto px-6 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2 text-sm font-medium"
          data-testid="tf-submit"
        >
          <Send className="w-4 h-4" />
          {submitting ? 'Submitting…' : 'Submit testimonial'}
        </button>
      </form>
    </PageShell>
  );
};

const PageShell = ({ children }) => (
  <div className="min-h-screen bg-gradient-to-br from-secondary/5 via-white to-primary/5">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 max-w-2xl">
      <div className="flex items-center gap-2 mb-6">
        <PawPrint className="w-6 h-6 text-primary" />
        <span className="font-bold text-foreground">Pawsome Ventures</span>
      </div>
      <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 border border-border">
        {children}
      </div>
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold text-foreground/70 mb-1 uppercase tracking-wide">{label}</label>
    {children}
  </div>
);
